import React, { useEffect, useState } from 'react';
import { Database, Plus, Trash2, Save, Loader2, Hammer, Package } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Loader } from '@/components/common/Feedback';
import api from '@/lib/api';
import { fmtMoney } from '@/lib/format';
import { toast } from 'sonner';

export default function CostDatabase() {
  const [db, setDb] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  useEffect(() => { api.get('/cost-database').then((r) => setDb(r.data)).finally(() => setLoading(false)); }, []);

  const mut = (fn) => { setDb((d) => { const c = JSON.parse(JSON.stringify(d)); fn(c); return c; }); setDirty(true); };

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.put('/cost-database', {
        labor_crafts: db.labor_crafts, materials: db.materials, markup_pct: Number(db.markup_pct) || 0,
      });
      setDb(r.data); setDirty(false);
      toast.success('Cost database saved');
    } catch (e) { toast.error('Save failed'); }
    finally { setSaving(false); }
  };

  if (loading) return <Loader label="Loading cost database..." />;
  if (!db) return null;

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-slate-500">Labor crafts, material price list, and default markup used by the AI estimating engine.</p>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <span className="text-sm text-slate-500">Default markup</span>
            <Input type="number" value={db.markup_pct || 0} onChange={(e) => { setDb({ ...db, markup_pct: Number(e.target.value) }); setDirty(true); }}
              className="h-9 w-20 text-right font-mono" data-testid="markup-input" />
            <span className="text-sm text-slate-400">%</span>
          </div>
          {dirty && <Button onClick={save} disabled={saving} className="bg-primary hover:bg-ink-700 h-9" data-testid="save-cost-db">
            {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}Save
          </Button>}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <Card className="p-0 overflow-hidden" data-testid="labor-crafts-card">
          <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200">
            <div className="flex items-center gap-2"><Hammer className="h-4 w-4 text-slate-500" /><span className="text-sm font-semibold text-slate-800">Labor Crafts &amp; Rates</span></div>
            <Button size="sm" variant="ghost" className="h-7 text-xs" data-testid="add-craft"
              onClick={() => mut((c) => c.labor_crafts.push({ craft: '', rate: 0 }))}><Plus className="h-3.5 w-3.5 mr-1" />Add craft</Button>
          </div>
          <table className="w-full text-sm">
            <thead className="bg-[#FAFBFC] text-[11px] font-semibold uppercase tracking-wide text-slate-500"><tr><th className="text-left px-4 py-2">Craft</th><th className="text-right px-4 py-2 w-32">Rate ($/hr)</th><th className="w-10" /></tr></thead>
            <tbody className="divide-y divide-slate-100">
              {db.labor_crafts.map((l, i) => (
                <tr key={i}>
                  <td className="px-4 py-1.5"><Input value={l.craft} onChange={(e) => mut((c) => c.labor_crafts[i].craft = e.target.value)} className="h-8" data-testid={`craft-name-${i}`} /></td>
                  <td className="px-4 py-1.5"><Input type="number" value={l.rate} onChange={(e) => mut((c) => c.labor_crafts[i].rate = Number(e.target.value))} className="h-8 text-right font-mono" data-testid={`craft-rate-${i}`} /></td>
                  <td className="px-2 text-right"><button onClick={() => mut((c) => c.labor_crafts.splice(i, 1))} className="text-slate-300 hover:text-red-500" data-testid={`del-craft-${i}`}><Trash2 className="h-3.5 w-3.5" /></button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>

        <Card className="p-0 overflow-hidden" data-testid="materials-card">
          <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200">
            <div className="flex items-center gap-2"><Package className="h-4 w-4 text-slate-500" /><span className="text-sm font-semibold text-slate-800">Material Price List</span></div>
            <Button size="sm" variant="ghost" className="h-7 text-xs" data-testid="add-material"
              onClick={() => mut((c) => c.materials.push({ item: '', unit: 'EA', unit_price: 0 }))}><Plus className="h-3.5 w-3.5 mr-1" />Add material</Button>
          </div>
          <table className="w-full text-sm">
            <thead className="bg-[#FAFBFC] text-[11px] font-semibold uppercase tracking-wide text-slate-500"><tr><th className="text-left px-4 py-2">Item</th><th className="text-left px-2 py-2 w-16">Unit</th><th className="text-right px-4 py-2 w-28">Price</th><th className="w-10" /></tr></thead>
            <tbody className="divide-y divide-slate-100">
              {db.materials.map((m, i) => (
                <tr key={i}>
                  <td className="px-4 py-1.5"><Input value={m.item} onChange={(e) => mut((c) => c.materials[i].item = e.target.value)} className="h-8" data-testid={`mat-name-${i}`} /></td>
                  <td className="px-2 py-1.5"><Input value={m.unit} onChange={(e) => mut((c) => c.materials[i].unit = e.target.value)} className="h-8 w-14" /></td>
                  <td className="px-4 py-1.5"><Input type="number" value={m.unit_price} onChange={(e) => mut((c) => c.materials[i].unit_price = Number(e.target.value))} className="h-8 text-right font-mono" data-testid={`mat-price-${i}`} /></td>
                  <td className="px-2 text-right"><button onClick={() => mut((c) => c.materials.splice(i, 1))} className="text-slate-300 hover:text-red-500" data-testid={`del-mat-${i}`}><Trash2 className="h-3.5 w-3.5" /></button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      </div>
    </div>
  );
}
